import {
  NODE_SUB_PURPOSE_MAP
} from "./chunk-NUTMEM5W.js";

// src/graph/index.ts
import { z as z5 } from "zod";

// src/graph/timeslice.ts
import { z } from "zod";
var TIMESLICE_CLASS = { effect: "TimeSliceEffect", wave: "TimeSliceWave", dual: "TimeSliceDual" };
var TIMESLICE_NODE_BY_WORKFLOW = { "time-slice": "effect", "time-wave": "wave", "time-slice-dual": "dual" };
var CURVES = ["linear", "ease_in", "ease_out", "ease_in_out", "exponential"];
var SPACINGS = ["linear", "ease_in", "ease_out", "ease_in_out", "random"];
var DIRECTIONS = ["forward", "reverse", "center_out", "edges_in"];
var LOOP_MODES = ["loop", "ping_pong", "trim"];
var PATTERNS = ["alternate", "pairs", "aba", "aabb", "abba"];
var TIMESLICE_OPTION_LABELS = {
  linear: "Linear",
  ease_in: "Ease in",
  ease_out: "Ease out",
  ease_in_out: "Ease in and out",
  exponential: "Exponential",
  random: "Random",
  forward: "Forward",
  reverse: "Reverse",
  center_out: "Center out",
  edges_in: "Edges in",
  loop: "Loop",
  ping_pong: "Ping-pong",
  trim: "Trim",
  alternate: "Alternate",
  pairs: "Pairs",
  aba: "A B A",
  aabb: "A A B B",
  abba: "A B B A"
};
var whole = (min, max) => ({ kind: "int", min, max, step: 1 });
var SLICES = whole(1, 2e3);
var OFFSET = whole(-120, 120);
var PEAK = whole(1, 120);
var BLEND = whole(0, 400);
var SEED = whole(0, 4294967295);
var JITTER = whole(0, 60);
var FRAMES = whole(0, 1e5);
var ANGLE = { kind: "float", min: -180, max: 180, step: 1 };
var SPEED = { kind: "float", min: 0, max: 50, step: 0.25 };
var COUNT = { kind: "float", min: 0.25, max: 20, step: 0.25 };
var MIX = { kind: "float", min: 0, max: 1, step: 0.05 };
var num = (id, label, range, value, hero = false) => ({ id, label, hero, ...range, default: value });
var choice = (id, label, options, hero = false) => ({ id, label, hero, kind: "select", options, default: options[0] });
var flag = (id, label, value) => ({ id, label, hero: false, kind: "boolean", default: value });
var TIMESLICE_PARAMS = {
  effect: [
    num("num_slices", "Slices", SLICES, 20, true),
    num("offset", "Frame offset", OFFSET, 1, true),
    num("angle", "Angle", ANGLE, 0, true),
    choice("spacing", "Slice spacing", SPACINGS),
    choice("direction", "Direction", DIRECTIONS, true),
    choice("loop_mode", "Loop mode", LOOP_MODES, true),
    num("blend_width", "Blend width", BLEND, 0),
    num("seed", "Seed", SEED, 0),
    choice("cascade_curve", "Cascade curve", CURVES),
    num("slice_jitter", "Slice jitter", JITTER, 0),
    flag("animate", "Animate", false),
    num("num_slices_end", "Slices at end", SLICES, 60),
    num("offset_end", "Frame offset at end", OFFSET, 6),
    num("angle_end", "Angle at end", ANGLE, 0),
    choice("temporal_curve", "Animation curve", CURVES),
    num("frame_count", "Animation length", FRAMES, 0)
  ],
  wave: [
    num("num_slices", "Slices", SLICES, 30, true),
    num("max_offset", "Peak offset", PEAK, 8, true),
    num("wave_speed", "Wave speed", SPEED, 2, true),
    num("wave_count", "Wave count", COUNT, 1, true),
    num("angle", "Angle", ANGLE, 0, true),
    choice("loop_mode", "Loop mode", LOOP_MODES, true),
    num("blend_width", "Blend width", BLEND, 0),
    choice("spacing", "Slice spacing", SPACINGS),
    num("seed", "Seed", SEED, 0)
  ],
  dual: [
    num("num_slices", "Slices", SLICES, 20, true),
    num("offset_a", "Clip A offset", OFFSET, 1, true),
    num("offset_b", "Clip B offset", OFFSET, 1, true),
    num("angle", "Angle", ANGLE, 0),
    choice("spacing", "Slice spacing", SPACINGS),
    choice("direction", "Direction", DIRECTIONS),
    choice("pattern", "Pattern", PATTERNS, true),
    choice("loop_mode", "Loop mode", LOOP_MODES, true),
    num("blend_width", "Blend width", BLEND, 0),
    num("mix", "Mix", MIX, 0.5, true),
    num("seed", "Seed", SEED, 0),
    choice("cascade_curve", "Cascade curve", CURVES),
    num("slice_jitter", "Slice jitter", JITTER, 0),
    flag("animate", "Animate", false),
    num("num_slices_end", "Slices at end", SLICES, 60),
    num("offset_a_end", "Clip A offset at end", OFFSET, 6),
    num("offset_b_end", "Clip B offset at end", OFFSET, 6),
    num("angle_end", "Angle at end", ANGLE, 0),
    choice("temporal_curve", "Animation curve", CURVES),
    num("frame_count", "Animation length", FRAMES, 0)
  ]
};
var TIMESLICE_DEFAULTS = Object.fromEntries(
  ["dual", "wave", "effect"].flatMap((node) => TIMESLICE_PARAMS[node].map((p) => [p.id, p.default]))
);
var number = (range) => range.kind === "int" ? z.number().int().min(range.min).max(range.max) : z.number().min(range.min).max(range.max);
var TimeSliceEffectParamsSchema = z.object({
  num_slices: number(SLICES),
  offset: number(OFFSET),
  angle: number(ANGLE),
  spacing: z.enum(SPACINGS),
  direction: z.enum(DIRECTIONS),
  loop_mode: z.enum(LOOP_MODES),
  blend_width: number(BLEND),
  seed: number(SEED),
  cascade_curve: z.enum(CURVES),
  slice_jitter: number(JITTER),
  animate: z.boolean(),
  num_slices_end: number(SLICES),
  offset_end: number(OFFSET),
  angle_end: number(ANGLE),
  temporal_curve: z.enum(CURVES),
  frame_count: number(FRAMES)
}).strict();
var TimeSliceWaveParamsSchema = z.object({
  num_slices: number(SLICES),
  max_offset: number(PEAK),
  wave_speed: number(SPEED),
  wave_count: number(COUNT),
  angle: number(ANGLE),
  loop_mode: z.enum(LOOP_MODES),
  blend_width: number(BLEND),
  spacing: z.enum(SPACINGS),
  seed: number(SEED)
}).strict();
var TimeSliceDualParamsSchema = z.object({
  num_slices: number(SLICES),
  offset_a: number(OFFSET),
  offset_b: number(OFFSET),
  angle: number(ANGLE),
  spacing: z.enum(SPACINGS),
  direction: z.enum(DIRECTIONS),
  pattern: z.enum(PATTERNS),
  loop_mode: z.enum(LOOP_MODES),
  blend_width: number(BLEND),
  mix: number(MIX),
  seed: number(SEED),
  cascade_curve: z.enum(CURVES),
  slice_jitter: number(JITTER),
  animate: z.boolean(),
  num_slices_end: number(SLICES),
  offset_a_end: number(OFFSET),
  offset_b_end: number(OFFSET),
  angle_end: number(ANGLE),
  temporal_curve: z.enum(CURVES),
  frame_count: number(FRAMES)
}).strict();
function roundHalfEven(x) {
  const floor = Math.floor(x);
  const rest = x - floor;
  const rounded = rest > 0.5 ? floor + 1 : rest < 0.5 ? floor : floor % 2 === 0 ? floor : floor + 1;
  return rounded + 0;
}
function timesliceEase(t, curve) {
  if (curve === "ease_in") return t * t;
  if (curve === "ease_out") {
    const v = 1 - t;
    return 1 - v * v;
  }
  if (curve === "ease_in_out") {
    if (t < 0.5) return 2 * t * t;
    const v = -2 * t + 2;
    return 1 - v * v / 2;
  }
  if (curve === "exponential") return Math.expm1(3 * t) / Math.expm1(3);
  return t;
}
var lerp = (a, b, t) => a + (b - a) * t;
var floorMod = (a, n) => (a % n + n) % n;
var NumpyRandomState = class {
  words = new Uint32Array(624);
  index = 624;
  constructor(seed) {
    const mt = this.words;
    mt[0] = seed >>> 0;
    for (let i = 1; i < 624; i++) {
      const prev = mt[i - 1];
      mt[i] = Math.imul(1812433253, prev ^ prev >>> 30) + i >>> 0;
    }
  }
  twist() {
    const mt = this.words;
    for (let k = 0; k < 624; k++) {
      const y = mt[k] & 2147483648 | mt[(k + 1) % 624] & 2147483647;
      mt[k] = mt[(k + 397) % 624] ^ y >>> 1 ^ (y & 1 ? 2567483615 : 0);
    }
    this.index = 0;
  }
  /** The next raw 32-bit word. */
  nextUint32() {
    if (this.index >= 624) this.twist();
    let y = this.words[this.index++];
    y ^= y >>> 11;
    y ^= y << 7 & 2636928640;
    y ^= y << 15 & 4022730752;
    y ^= y >>> 18;
    return y >>> 0;
  }
  /** random_sample(): 53 random bits in [0, 1); uniform(0, 1) draws the same values. */
  random() {
    const a = this.nextUint32() >>> 5;
    const b = this.nextUint32() >>> 6;
    return (a * 67108864 + b) / 9007199254740992;
  }
  /** A whole number in [0, max] by masked rejection (numpy's random_interval). */
  interval(max) {
    if (max === 0) return 0;
    let mask = max;
    mask |= mask >>> 1;
    mask |= mask >>> 2;
    mask |= mask >>> 4;
    mask |= mask >>> 8;
    mask |= mask >>> 16;
    for (; ; ) {
      const value = (this.nextUint32() & mask) >>> 0;
      if (value <= max) return value;
    }
  }
  /** randint(low, high, size) into int64, for ranges under 2^32 (the legacy masked path). */
  randint(low, high, count) {
    return Array.from({ length: count }, () => low + this.interval(high - 1 - low));
  }
  /** shuffle(list), in place, as for a Python list. */
  shuffle(list) {
    for (let i = list.length - 1; i > 0; i--) {
      const j = this.interval(i);
      const kept = list[i];
      list[i] = list[j];
      list[j] = kept;
    }
  }
};
function sliceOffsets(slices, offset, direction, curve, jitter, seed) {
  let base;
  if (slices <= 1) base = new Array(Math.max(slices, 1)).fill(0);
  else {
    base = Array.from({ length: slices }, (_, i) => {
      const u = i / (slices - 1);
      const a = direction === "reverse" ? 1 - u : direction === "center_out" ? Math.abs(2 * u - 1) : direction === "edges_in" ? 1 - Math.abs(2 * u - 1) : u;
      return roundHalfEven(timesliceEase(a, curve) * (slices - 1) * offset);
    });
  }
  if (jitter > 0) {
    const jitters = new NumpyRandomState(seed + 1234 >>> 0).randint(-jitter, jitter + 1, base.length);
    base = base.map((value, i) => value + jitters[i]);
  }
  return base;
}
function resolveFrame(raw, total, loopMode) {
  if (total <= 1) return 0;
  if (loopMode === "loop") return floorMod(raw, total);
  if (loopMode === "ping_pong") {
    const cycle = 2 * total - 2;
    const at = floorMod(raw, cycle);
    return at < total ? at : cycle - at;
  }
  return Math.max(0, Math.min(raw, total - 1));
}
function sourcePattern(slices, pattern) {
  const cycle = pattern === "pairs" ? [0, 0, 1, 1] : pattern === "aba" ? [0, 1, 0] : pattern === "aabb" ? [0, 0, 1, 1] : pattern === "abba" ? [0, 1, 1, 0] : [0, 1];
  return Array.from({ length: slices }, (_, i) => cycle[i % cycle.length]);
}
function biasedSourceMap(slices, pattern, mix, seed) {
  const map = sourcePattern(slices, pattern);
  if (mix === 0.5) return map;
  const bCount = map.reduce((sum, clip) => sum + clip, 0);
  const targetB = Math.max(0, Math.min(slices, roundHalfEven(slices * mix)));
  const random = new NumpyRandomState(seed + 9999 >>> 0);
  const flip = (from, count) => {
    const indices = map.flatMap((clip, i) => clip === from ? [i] : []);
    random.shuffle(indices);
    for (const i of indices.slice(0, count)) map[i] = 1 - from;
  };
  if (bCount < targetB) flip(0, targetB - bCount);
  else if (bCount > targetB) flip(1, bCount - targetB);
  return map;
}
function animationAt(t, length, curve) {
  return timesliceEase(Math.min(1, t / Math.max(1, length - 1)), curve);
}
function extent(rows) {
  let min = Infinity, max = -Infinity;
  for (const row of rows) for (const value of row) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return { min, max };
}
function planEffect(p, frames) {
  const length = Math.max(1, p.animate && p.frame_count > 0 ? p.frame_count : frames);
  const at = (t) => {
    if (!p.animate) return { slices: p.num_slices, offset: p.offset, angle: p.angle };
    const tau = animationAt(t, length, p.temporal_curve);
    return { slices: Math.max(1, roundHalfEven(lerp(p.num_slices, p.num_slices_end, tau))), offset: roundHalfEven(lerp(p.offset, p.offset_end, tau)), angle: lerp(p.angle, p.angle_end, tau) };
  };
  const cache = /* @__PURE__ */ new Map();
  const offsets = Array.from({ length: frames }, (_, t) => {
    const { slices, offset } = at(t);
    const key = `${slices}:${offset}`;
    let row = cache.get(key);
    if (!row) cache.set(key, row = sliceOffsets(slices, offset, p.direction, p.cascade_curve, p.slice_jitter, p.seed));
    return row;
  });
  const { min, max } = extent(offsets);
  const numOutput = p.loop_mode === "trim" ? Math.max(1, frames - (max - min)) : frames;
  return {
    node: "effect",
    numOutput,
    audioShift: 0 - min,
    offsets,
    frame(t) {
      const { slices, angle } = at(t);
      return { slices, angle, source: offsets[t].map((offset) => resolveFrame(t + offset - min, frames, p.loop_mode)) };
    }
  };
}
function planWave(p, frames) {
  const trim = p.loop_mode === "trim";
  const numOutput = trim ? Math.max(1, frames - 2 * p.max_offset) : frames;
  const pad = trim ? p.max_offset : 0;
  return {
    node: "wave",
    numOutput,
    audioShift: pad,
    frame(t) {
      const phase = p.wave_speed * t / Math.max(1, numOutput);
      const source = Array.from({ length: p.num_slices }, (_, i) => {
        const offset = roundHalfEven(p.max_offset * Math.sin(2 * Math.PI * (p.wave_count * i / Math.max(1, p.num_slices) + phase)));
        return resolveFrame(t + pad + offset, frames, p.loop_mode);
      });
      return { slices: p.num_slices, angle: p.angle, source };
    }
  };
}
function planDual(p, framesA, framesB) {
  const shorter = Math.min(framesA, framesB);
  const length = Math.max(1, p.animate && p.frame_count > 0 ? p.frame_count : shorter);
  const at = (t) => {
    if (!p.animate) return { slices: p.num_slices, a: p.offset_a, b: p.offset_b, angle: p.angle };
    const tau = animationAt(t, length, p.temporal_curve);
    return {
      slices: Math.max(1, roundHalfEven(lerp(p.num_slices, p.num_slices_end, tau))),
      a: roundHalfEven(lerp(p.offset_a, p.offset_a_end, tau)),
      b: roundHalfEven(lerp(p.offset_b, p.offset_b_end, tau)),
      angle: lerp(p.angle, p.angle_end, tau)
    };
  };
  const offsets = [], maps = [], chosen = [];
  let aLo = 1e9, aHi = -1e9, bLo = 1e9, bHi = -1e9;
  for (let t = 0; t < shorter; t++) {
    const { slices, a, b } = at(t);
    const map = biasedSourceMap(slices, p.pattern, p.mix, p.seed);
    const offA = sliceOffsets(slices, a, p.direction, p.cascade_curve, p.slice_jitter, p.seed);
    const offB = sliceOffsets(slices, b, p.direction, p.cascade_curve, p.slice_jitter, p.seed + 7);
    const row = map.map((clip, i) => clip === 0 ? offA[i] : offB[i]);
    const onA = row.filter((_, i) => map[i] === 0), onB = row.filter((_, i) => map[i] === 1);
    for (const offset of onA.length ? onA : [0]) {
      aLo = Math.min(aLo, offset);
      aHi = Math.max(aHi, offset);
    }
    for (const offset of onB.length ? onB : [0]) {
      bLo = Math.min(bLo, offset);
      bHi = Math.max(bHi, offset);
    }
    offsets.push([offA, offB]);
    maps.push(map);
    chosen.push(row);
  }
  const numOutput = p.loop_mode === "trim" ? Math.min(Math.max(1, framesA - (aHi - aLo)), Math.max(1, framesB - (bHi - bLo))) : shorter;
  return {
    node: "dual",
    numOutput,
    audioShift: 0 - aLo,
    offsets,
    maps,
    frame(t) {
      const { slices, angle } = at(t);
      const map = maps[t];
      const source = chosen[t].map((offset, i) => map[i] === 0 ? resolveFrame(t + offset - aLo, framesA, p.loop_mode) : resolveFrame(t + offset - bLo, framesB, p.loop_mode));
      return { slices, angle, source, clip: map };
    }
  };
}
function timesliceMemoryBytes(frames, width, height, node) {
  return frames * width * height * 3 * 4 * (node === "dual" ? 3 : 2);
}

// src/graph/node-defs.ts
var GENERATION_OPERATIONS = [
  "gen.image",
  "gen.video",
  "gen.sound",
  "gen.3d",
  "edit.instruct",
  "edit.variation",
  "edit.inpaint",
  "edit.expand",
  "edit.detail",
  "edit.upscale"
];
var DEFS = {
  "trigger.manual": {
    label: "Manual",
    category: "Triggers",
    icon: "play",
    inputs: [],
    outputs: [{ id: "out", type: "any" }],
    params: {}
  },
  "trigger.schedule": {
    label: "Schedule",
    category: "Triggers",
    icon: "clock",
    inputs: [],
    outputs: [{ id: "out", type: "any" }],
    params: { everyMinutes: 60 }
  },
  "trigger.watch": {
    label: "Watch folder",
    category: "Triggers",
    icon: "eye",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { path: "inbox" }
  },
  "canvas.in": {
    label: "Canvas In",
    category: "Canvas",
    icon: "frame",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { artboardId: "" }
  },
  // A clip on the Board. Same run as Canvas In; its video output is what lets it wire into a video-only input.
  "canvas.in-video": {
    label: "Canvas in (video)",
    category: "Canvas",
    icon: "film",
    inputs: [],
    outputs: [{ id: "out", type: "video" }],
    params: { artboardId: "" }
  },
  // The pictures a section holds (Board frames inside its box) or a Compositor holds (its layers); frameIds
  // (comma-separated) narrows them, or stands alone as the frames to work on.
  "canvas.section": {
    label: "Section In",
    category: "Canvas",
    icon: "section",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { sectionId: "", frameIds: "" }
  },
  "canvas.note": {
    label: "Note In",
    category: "Canvas",
    icon: "note",
    inputs: [],
    outputs: [{ id: "out", type: "text" }],
    params: { artboardId: "" }
  },
  // Every frame that carries a note (a note anchored to it), the note's words riding on the item as its
  // instruction; the edit nodes take them when their own prompt is empty. A section narrows it.
  "canvas.notes": {
    label: "Notes In",
    category: "Canvas",
    icon: "note",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { sectionId: "" }
  },
  "canvas.out": {
    label: "Canvas Out",
    category: "Canvas",
    icon: "frame",
    inputs: [{ id: "in", type: "image" }],
    outputs: [],
    params: { mode: "new", artboardId: "" }
    // 'new' artboards | 'stack' versions on one frame | 'source' back on each item's own frame
  },
  "gen.image": {
    label: "Generate image",
    category: "Generate",
    icon: "sparkles",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "text" }, { id: "ref", type: "image" }, { id: "control", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", width: 512, height: 512, count: 1, provider: "", model: "", engine: "", control: "" }
  },
  "gen.video": {
    label: "Animate",
    category: "Generate",
    icon: "film",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }, { id: "text", type: "text" }],
    outputs: [{ id: "out", type: "video" }],
    params: { prompt: "", seconds: 5, engine: "" }
  },
  "gen.sound": {
    label: "Sound",
    category: "Generate",
    icon: "music",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "text" }],
    outputs: [{ id: "out", type: "audio" }],
    params: { prompt: "", seconds: 10, engine: "" }
  },
  "gen.3d": {
    label: "Image \u2192 3D",
    category: "Generate",
    icon: "box",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "mesh" }],
    params: { engine: "cloud" }
  },
  "edit.instruct": {
    label: "Edit with words",
    category: "Edit",
    icon: "wand",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }, { id: "ref", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", engine: "", keepRest: false, context: false }
  },
  "edit.erase": {
    label: "Erase",
    category: "Edit",
    icon: "eraser",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "mask", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { engine: "", keepRest: true }
  },
  // Cut by a selection, no model: keep the selection (the rest transparent) or cut it out, the edge feathered.
  "edit.cutout": {
    label: "Cut out",
    category: "Edit",
    icon: "scissors",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "mask", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { mode: "keep", feather: 2 }
  },
  "edit.segment": {
    label: "Select subject",
    category: "Edit",
    icon: "scan",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", points: "", engine: "" }
  },
  "edit.detail": {
    label: "Face detail",
    category: "Edit",
    icon: "smile",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", strength: 0.4, engine: "" }
  },
  "edit.interpolate": {
    label: "Smooth motion",
    category: "Edit",
    icon: "fast-forward",
    app: true,
    inputs: [{ id: "in", type: "video" }],
    outputs: [{ id: "out", type: "video" }],
    params: { multiplier: 2, engine: "" }
  },
  "edit.vectorize": {
    label: "Vectorize",
    category: "Edit",
    icon: "pen-tool",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { colors: 16, detail: "balanced", engine: "" }
  },
  "text.describe": {
    label: "Describe image",
    category: "Text",
    icon: "eye",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "text" }],
    params: { engine: "", model: "" }
    // model: a GET /prompt-tools choice id; empty = the usual order
  },
  "text.enhance": {
    label: "Enhance prompt",
    category: "Text",
    icon: "sparkle",
    app: true,
    inputs: [{ id: "in", type: "text" }],
    outputs: [{ id: "out", type: "text" }],
    params: { style: "", model: "" }
  },
  // A hosted model tool from the engine's hosted-tool table; `tool` is its slug, the rest are that tool's options.
  "tool.run": {
    label: "Model tool",
    category: "Generate",
    icon: "wand-2",
    app: true,
    inputs: [{ id: "in", type: "any" }, { id: "ref", type: "any" }, { id: "audio", type: "any" }, { id: "video", type: "any" }],
    outputs: [{ id: "out", type: "any" }],
    params: { tool: "", model: "", prompt: "", lighting: "", garment: "", pose: "", scribble: false, influence: 0.75, quality: "", scale: "", direction: "", seconds: 5, aspect: "", task: "", language: "", voice: "", polycount: 3e4 }
  },
  "edit.variation": {
    label: "Variation",
    category: "Edit",
    icon: "shuffle",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { creativity: 0.5, prompt: "" }
  },
  "edit.inpaint": {
    label: "Inpaint",
    category: "Edit",
    icon: "paintbrush",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }, { id: "mask", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", denoise: 1, growMask: 8, keepRest: true }
  },
  "edit.move": {
    label: "Move object",
    category: "Edit",
    icon: "move",
    inputs: [{ id: "in", type: "image" }, { id: "mask", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { dx: 0, dy: 0, blend: true }
  },
  "edit.expand": {
    label: "Expand",
    category: "Edit",
    icon: "expand",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { left: 0, right: 0, top: 0, bottom: 0, prompt: "" }
  },
  "edit.upscale": {
    label: "Upscale",
    category: "Edit",
    icon: "arrow-up",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { scale: 2, method: "fast", creativity: 0.3, engine: "", tiles: "auto" }
  },
  "edit.rembg": {
    label: "Remove background",
    category: "Edit",
    icon: "eraser",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "ref", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { engine: "", beneath: false }
  },
  "edit.bgfree": {
    label: "Repaint without background",
    category: "Edit",
    icon: "sparkles",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", match: true, fallback: "cut" }
  },
  "edit.adjust": {
    label: "Adjust",
    category: "Edit",
    icon: "sliders",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { brightness: 1, contrast: 1, saturation: 1, hue: 0, temperature: 0, blur: 0, sharpen: 0, grayscale: false }
  },
  // Cut-out care: instant, no model. What a lifted subject needs afterwards.
  "edit.alpha": {
    label: "Clean cut-out",
    category: "Edit",
    icon: "eraser",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { specks: 400, faint: true, defringe: true, edge: "none" }
    // specks: area in px, 0 keeps them; edge: none | soften | harden
  },
  "edit.match": {
    label: "Match tone",
    category: "Edit",
    icon: "palette",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "ref", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    // strength: how far the mean colour moves toward the reference, 0..1. beneath: the reference is the ring round
    // the frame on the board. when: 'always', or 'off' (only a frame flagged tone or much brighter than the ring).
    params: { strength: 0.5, beneath: false, when: "always" }
  },
  "edit.shadow": {
    label: "Contact shadow",
    category: "Edit",
    icon: "sun-dim",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    // of the cut-out's width; the shadow is its own layer. ground: write it as the frame's ground layer (when it has none) and pass the picture through.
    params: { strength: 0.34, width: 0.36, height: 0.075, ground: false }
  },
  // The night grade: a frame's mid-tones pulled toward the board's light at its foot, highlights kept, a share of the cast, never brightening.
  "edit.grade": {
    label: "Grade to light",
    category: "Edit",
    icon: "sun-dim",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "ref", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { strength: 0.75, lift: 14, cast: 0.4 }
    // ref: the picture the light field is read from; without one, the board's background frame
  },
  // An audit over frames: flags written on them (bg, cut, tone, ground, foot, lost), the numbers in the item's meta, the picture passed through.
  "edit.inspect": {
    label: "Inspect",
    category: "Edit",
    icon: "search",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { checks: "bg,cut,tone,ground,foot,lost", write: true }
  },
  // Data Dune: the desktop places a live WebGPU frame for it; the engine DAG cannot execute it.
  "render.pigment": {
    label: "Data Dune",
    category: "Edit",
    icon: "wind",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { look: "drift", turbulence: 1.5, wanderRadius: 0.075, masterSize: 0.92, relief: 0.9, climbHeight: 0.3, seed: 1 }
  },
  // Nudge (graph/nudge.ts): published ComfyUI effects, run on this Mac. Each app pins its workflow;
  // neither operation is in Flow's palette yet.
  "nudge.image": {
    label: "Nudge picture",
    category: "Edit",
    icon: "sparkle",
    legacy: true,
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { workflow: "", subject: "", seed: -1, megapixels: 1.6, strength: 1, lightning: 1, steps: 8, target_size: "1344" }
  },
  "nudge.video": {
    label: "Nudge clip",
    category: "Edit",
    icon: "film",
    legacy: true,
    app: true,
    inputs: [{ id: "in", type: "video" }, { id: "b", type: "video" }],
    outputs: [{ id: "out", type: "video" }],
    params: { workflow: "", ...TIMESLICE_DEFAULTS }
  },
  "edit.tracy": {
    label: "Tracy",
    category: "Edit",
    icon: "scan",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { options: "{}" }
  },
  "edit.crop": {
    label: "Crop",
    category: "Edit",
    icon: "crop",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { x: 0, y: 0, w: 1, h: 1 }
    // fractions of the source
  },
  "edit.transform": {
    label: "Flip & rotate",
    category: "Edit",
    icon: "rotate",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { flipH: false, flipV: false, rotate: 0 }
  },
  "edit.split-grid": {
    label: "Split image grid",
    category: "Edit",
    icon: "grid",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { columns: 2, rows: 2 }
  },
  // Retired from the palette: stacking images is the Layers editor now. Saved graphs keep running.
  "compose.composite": {
    label: "Composite",
    category: "Compose",
    icon: "layers",
    legacy: true,
    app: true,
    inputs: [{ id: "a", type: "image" }, { id: "b", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { anchor: "center", scalePct: 50, opacity: 1, blend: "over" }
    // a = base, b = overlay
  },
  "compose.text": {
    label: "Text overlay",
    category: "Compose",
    icon: "type",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { text: "", size: 64, color: "#ffffff", anchor: "bottom", bold: true }
    // {{text}} → item.text
  },
  "compose.resize": {
    label: "Resize",
    category: "Compose",
    icon: "maximize",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { width: 1024, height: 1024, fit: "cover" }
  },
  "compose.adjust": {
    label: "Adjust",
    category: "Compose",
    icon: "sliders",
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { brightness: 1, saturation: 1, blur: 0, grayscale: false }
  },
  "text.prompt": {
    label: "Prompt",
    category: "Text",
    icon: "type",
    inputs: [{ id: "in", type: "text" }],
    outputs: [{ id: "out", type: "text" }],
    params: { text: "" }
    // {{text}} is replaced with the incoming item's text
  },
  "logic.if": {
    label: "Filter",
    category: "Logic",
    icon: "filter",
    inputs: [{ id: "in", type: "any" }],
    outputs: [{ id: "yes", type: "any" }, { id: "no", type: "any" }],
    params: { contains: "" }
    // matched against text / title / prompt
  },
  "logic.merge": {
    label: "Merge",
    category: "Logic",
    icon: "merge",
    inputs: [{ id: "a", type: "any" }, { id: "b", type: "any" }],
    outputs: [{ id: "out", type: "any" }],
    params: {}
  },
  "files.read": {
    label: "Read folder",
    category: "Files",
    icon: "folder",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { path: "outputs" }
  },
  "files.save": {
    label: "Save to folder",
    category: "Files",
    icon: "save",
    inputs: [{ id: "in", type: "image" }],
    outputs: [],
    params: { path: "exports" }
  }
};
for (const operation of GENERATION_OPERATIONS) DEFS[operation].params.generation = null;
var GRAPH_NODE_DEFS = DEFS;
var APP_OPERATIONS = Object.keys(DEFS).filter((type) => DEFS[type].app);
function graphNodeDef(type) {
  return Object.hasOwn(DEFS, type) ? DEFS[type] : void 0;
}
function customWorkflowDef(workflow) {
  const params = {};
  for (const item of workflow.params ?? []) params[item.key] = item.value;
  return {
    label: workflow.name,
    category: "ComfyUI",
    icon: "workflow",
    custom: workflow.id,
    inputs: [...workflow.inputs?.image ? [{ id: "in", type: "image" }] : [], { id: "text", type: "text" }],
    outputs: [{ id: "out", type: workflow.output ?? "image" }],
    params,
    exposed: workflow.params ?? []
  };
}
var CUSTOM_NODE_PREFIX = "comfy.custom:";
function resolveNodeDef(type, custom = {}) {
  const builtin = graphNodeDef(type);
  if (builtin) return builtin;
  if (!type.startsWith(CUSTOM_NODE_PREFIX)) return null;
  const id = type.slice(CUSTOM_NODE_PREFIX.length);
  return Object.hasOwn(custom, id) ? customWorkflowDef(custom[id]) : null;
}
function graphNodeDefs(custom = {}) {
  const defs = { ...GRAPH_NODE_DEFS };
  for (const id of Object.keys(custom)) defs[`${CUSTOM_NODE_PREFIX}${id}`] = customWorkflowDef(custom[id]);
  return defs;
}

// src/graph/desktop-update.ts
import { z as z2 } from "zod";
var DesktopUpdateStateSchema = z2.discriminatedUnion("status", [
  z2.object({ status: z2.literal("idle"), message: z2.string().optional() }),
  z2.object({ status: z2.literal("checking") }),
  z2.object({ status: z2.literal("available"), version: z2.string().min(1) }),
  z2.object({ status: z2.literal("downloading"), version: z2.string().min(1), progress: z2.number().min(0).max(1).optional() }),
  z2.object({ status: z2.literal("ready"), version: z2.string().min(1) }),
  z2.object({ status: z2.literal("installing"), version: z2.string().min(1) }),
  z2.object({ status: z2.literal("error"), message: z2.string(), retry: z2.enum(["check", "download", "install"]), version: z2.string().optional() })
]);

// src/graph/scene-assets.ts
import { z as z3 } from "zod";
var SceneAssetSchema = z3.object({
  id: z3.string().min(1),
  title: z3.string().min(1),
  author: z3.string().optional(),
  license: z3.object({ name: z3.string(), url: z3.string().optional(), commercial: z3.boolean().optional() }).optional()
});
var sceneFile = z3.string().regex(/^\/outputs\/[\w.-]+$/, "Expected a scene-owned output file");
var SceneEnvironmentSchema = SceneAssetSchema.extend({
  file: sceneFile,
  intensity: z3.number().min(0).max(5).default(1),
  background: z3.boolean().default(true)
});
var SceneMaterialSchema = SceneAssetSchema.extend({
  maps: z3.object({ color: sceneFile.optional(), normal: sceneFile.optional(), roughness: sceneFile.optional(), metalness: sceneFile.optional(), ao: sceneFile.optional() }),
  repeat: z3.number().min(0.1).max(20).default(1)
});
var SceneLibrarySettingsSchema = z3.object({
  environment: SceneEnvironmentSchema.nullable().optional(),
  material: SceneMaterialSchema.nullable().optional()
});
var ApplyLibraryAssetSchema = z3.object({ assetId: z3.string().min(1) });
var ShapeOptionsSchema = z3.object({
  shape: z3.enum(["box", "sphere", "torus", "knot", "cylinder", "cone", "capsule", "icosahedron"]),
  size: z3.number().min(0.1).max(10).default(1),
  detail: z3.number().int().min(8).max(64).default(32),
  twist: z3.number().min(-180).max(180).default(0),
  color: z3.string().regex(/^#[0-9a-f]{6}$/i).default("#b8b8b8"),
  metalness: z3.number().min(0).max(1).default(0.1),
  roughness: z3.number().min(0).max(1).default(0.35)
});

// src/graph/web-operations.ts
var WEB_NODE_TYPES = {
  ImageNode: "image",
  VideoNode: "video",
  ThreeDNode: "3d",
  TextNode: "text-llm"
};
var WEB_CANVAS_NODES = {
  FrameInput: "canvas.in",
  FrameOutput: "canvas.out"
};
var KEY = (nodeType, purpose, subPurpose) => subPurpose ? `${nodeType}/${purpose}/${subPurpose}` : `${nodeType}/${purpose}`;
var MAP = {
  // ── Image ────────────────────────────────────────────────────────────────
  [KEY("image", "create")]: { operation: "gen.image", app: "generate" },
  [KEY("image", "edit")]: { operation: "edit.instruct", app: "image-edit" },
  [KEY("image", "enhance")]: { operation: "edit.upscale", app: "upscale" },
  [KEY("image", "enhance", "upscale")]: { operation: "edit.upscale", app: "upscale" },
  [KEY("image", "enhance", "super-resolution")]: { operation: "edit.upscale", app: "upscale" },
  [KEY("image", "enhance", "sharpen")]: { operation: "edit.adjust", app: "sharpen" },
  [KEY("image", "tools", "inpaint")]: { operation: "edit.inpaint", app: "inpaint" },
  [KEY("image", "tools", "outpaint")]: { operation: "edit.expand", app: "expand" },
  [KEY("image", "tools", "background-removal")]: { operation: "edit.rembg", app: "remove-background" },
  [KEY("image", "tools", "object-removal")]: { operation: "edit.erase", app: "erase" },
  [KEY("image", "tools", "segmentation")]: { operation: "edit.segment", app: "segment" },
  [KEY("image", "tools", "captioning")]: { operation: "text.describe", app: "describe" },
  [KEY("image", "tools", "vectorization")]: { operation: "edit.vectorize", app: "vectorize" },
  [KEY("image", "tools", "face-retouching")]: { operation: "edit.detail", app: "enhance" },
  [KEY("image", "tools", "style-transfer")]: { operation: "edit.instruct", app: "styler" },
  [KEY("image", "tools", "reimagine")]: { operation: "tool.run", app: "reimagine" },
  [KEY("image", "tools", "colorization")]: { operation: "tool.run", app: "colorize" },
  [KEY("image", "tools", "relighting")]: { operation: "tool.run", app: "relight" },
  [KEY("image", "tools", "virtual-try-on")]: { operation: "tool.run", app: "virtual-try-on" },
  [KEY("image", "tools", "product-photography")]: { operation: "tool.run", app: "product-photo" },
  [KEY("image", "tools", "depth-estimation")]: { operation: "tool.run", app: "depth-map" },
  [KEY("image", "tools", "pose-estimation")]: { operation: "tool.run", app: "pose-map" },
  [KEY("image", "tools", "edge-detection")]: { operation: "tool.run", app: "edge-map" },
  // ── Video ────────────────────────────────────────────────────────────────
  [KEY("video", "create")]: { operation: "gen.video", app: "animate" },
  [KEY("video", "create", "text-to-video")]: { operation: "gen.video", app: "animate" },
  [KEY("video", "create", "image-to-video")]: { operation: "gen.video", app: "animate" },
  [KEY("video", "edit")]: { operation: "tool.run", app: "edit-video" },
  [KEY("video", "enhance")]: { operation: "tool.run", app: "upscale-video" },
  [KEY("video", "enhance", "video-upscale")]: { operation: "tool.run", app: "upscale-video" },
  [KEY("video", "tools", "frame-interpolation")]: { operation: "edit.interpolate", app: "smooth-video" },
  [KEY("video", "tools", "lipsync")]: { operation: "tool.run", app: "lipsync" },
  [KEY("video", "tools", "video-extend")]: { operation: "tool.run", app: "extend-video" },
  [KEY("video", "tools", "reframe")]: { operation: "tool.run", app: "reframe-video" },
  [KEY("video", "tools", "motion-transfer")]: { operation: "tool.run", app: "motion-transfer" },
  [KEY("video", "tools", "video-captioning")]: { operation: "tool.run", app: "describe-video" },
  // ── Sound ────────────────────────────────────────────────────────────────
  [KEY("sound", "create")]: { operation: "gen.sound", app: "sound" },
  [KEY("sound", "create", "text-to-music")]: { operation: "gen.sound", app: "sound" },
  [KEY("sound", "create", "text-to-sound")]: { operation: "gen.sound", app: "sound" },
  [KEY("sound", "enhance", "audio-enhance")]: { operation: "tool.run", app: "clean-audio" },
  [KEY("sound", "tools", "transcription")]: { operation: "tool.run", app: "transcribe" },
  [KEY("sound", "tools", "voice-conversion")]: { operation: "tool.run", app: "change-voice" },
  [KEY("sound", "tools", "audio-analysis")]: { operation: "tool.run", app: "analyze-audio" },
  // ── 3D ───────────────────────────────────────────────────────────────────
  [KEY("3d", "create")]: { operation: "gen.3d", app: "image-to-3d" },
  [KEY("3d", "create", "image-to-3d")]: { operation: "gen.3d", app: "image-to-3d" },
  // Text to 3D reaches the same operation without an image; no app offers it yet.
  [KEY("3d", "create", "text-to-3d")]: { operation: "gen.3d" },
  [KEY("3d", "edit", "remesh")]: { operation: "tool.run", app: "remesh" },
  [KEY("3d", "edit", "retexture")]: { operation: "tool.run", app: "retexture" },
  // ── Text ─────────────────────────────────────────────────────────────────
  [KEY("text-llm", "create")]: { operation: "text.enhance", app: "refine" },
  [KEY("text-llm", "edit")]: { operation: "text.enhance", app: "refine" }
};
var WEB_NODES_WITHOUT_OPERATION = {
  "image/tools/controlnet": "A guidance input to another operation, not an operation.",
  "image/tools/mask": "Its own editor; the mask it produces is an input.",
  "image/tools/face-swap": "No operation and no app offers it yet.",
  "image/tools/training": "Starts a training job, which outlives a graph run.",
  "image/enhance/denoise": "No operation offers it on its own yet.",
  "image/enhance/restore": "No operation offers it on its own yet.",
  "image/enhance/hdr": "No operation offers it on its own yet.",
  "video/tools/frame-extraction": "A player action on a clip, not a model run.",
  "video/tools/video-segmentation": "No operation offers it yet.",
  "video/tools/ffmpeg": "A container and codec utility, not a model run.",
  "video/tools/training": "Starts a training job, which outlives a graph run.",
  "sound/create/text-to-speech": "No operation offers a voice yet; generation covers music and sound.",
  "sound/tools/waveform": "A display of a clip, not a model run.",
  "3d/edit/part-editing": "Its own editor."
};
function webNodeOperation(nodeType, purpose, subPurpose) {
  if (Object.hasOwn(WEB_CANVAS_NODES, nodeType)) return { operation: WEB_CANVAS_NODES[nodeType] };
  const type = Object.hasOwn(WEB_NODE_TYPES, nodeType) ? WEB_NODE_TYPES[nodeType] : nodeType;
  if (!purpose) return null;
  if (subPurpose) {
    const exact = KEY(type, purpose, subPurpose);
    if (Object.hasOwn(WEB_NODES_WITHOUT_OPERATION, exact)) return null;
    if (Object.hasOwn(MAP, exact)) return MAP[exact];
  }
  const bare = KEY(type, purpose);
  return Object.hasOwn(MAP, bare) ? MAP[bare] : null;
}
var WEB_NODE_OPERATIONS = MAP;
function webOperationIssues() {
  return Object.entries(MAP).filter(([, value]) => !Object.hasOwn(GRAPH_NODE_DEFS, value.operation)).map(([key, value]) => `${key} names an unavailable operation: ${value.operation}.`);
}

// src/graph/execution-inventory.ts
var NO_EXECUTABLE_ADAPTER = "NO_EXECUTABLE_ADAPTER";
var LOCAL_CANVAS_FAMILIES = [
  "FrameInput",
  "FrameOutput",
  "TextFile",
  "ImageFile",
  "VideoFile",
  "AudioFile",
  "CommentNode",
  "MaskNode",
  "DataTable",
  "VariationGenerator",
  "PromptComposer",
  "BatchGenerator",
  "GalleryGroup",
  "GalleryContainer",
  "LoraModel",
  "ShaderNode",
  "shader"
];
var LOCAL_FAMILY = new Set(LOCAL_CANVAS_FAMILIES);
var PURPOSES = ["create", "edit", "enhance", "tools"];
var UNAVAILABLE_EXPLANATION = {
  sound: "Web sound has no verified request schema, queue type, adapter, persisted result, and canvas output, so it does not enter the generic job queue.",
  "3d": "Web 3D has no verified request schema, queue type, adapter, persisted result, and canvas renderer, so it does not enter the generic job queue."
};
var LOCAL_EXPLANATION = "This is an input, editor, or workflow on the canvas, not a standalone generation. It does not enter the generic job queue.";
function isStructuralNonOperation(explanation) {
  const text = explanation.toLowerCase();
  return text.includes("not an operation") || text.includes("its own editor") || text.includes("outlives a graph run") || text.includes("not a model run") || text.includes("display of a clip");
}
function normalizeFamily(nodeType) {
  if (!nodeType) return void 0;
  const trimmed = nodeType.trim();
  if (!trimmed) return void 0;
  if (Object.hasOwn(WEB_NODE_TYPES, trimmed)) return WEB_NODE_TYPES[trimmed];
  if (trimmed === "text") return "text-llm";
  return trimmed;
}
function routeFor(nodeType, purpose, subPurpose) {
  if (nodeType === "sound" || nodeType === "3d") {
    return { route: "unavailable", explanation: UNAVAILABLE_EXPLANATION[nodeType] };
  }
  if (nodeType === "text-llm") {
    return {
      route: "direct",
      explanation: "Text streams through the AI API. It is not a generic job-queue operation."
    };
  }
  if (subPurpose) {
    const key = `${nodeType}/${purpose}/${subPurpose}`;
    const excused = WEB_NODES_WITHOUT_OPERATION[key];
    if (excused && isStructuralNonOperation(excused)) {
      return { route: "local", explanation: excused };
    }
    if (excused) {
      return {
        route: "queued",
        explanation: `${excused} A selected hosted model may still use the generic queue; that is not a verified typed adapter.`
      };
    }
  }
  return {
    route: "queued",
    explanation: "Generic image or video queue. Reachability is not a claim that every visible setting hits a typed adapter."
  };
}
function executionInventory() {
  const entries = [];
  for (const [nodeType, purposes] of Object.entries(NODE_SUB_PURPOSE_MAP)) {
    for (const purpose of PURPOSES) {
      const subs = purposes[purpose] ?? [];
      if (subs.length === 0) {
        const classified = routeFor(nodeType, purpose);
        entries.push({
          key: `${nodeType}/${purpose}`,
          nodeType,
          purpose,
          route: classified.route,
          explanation: classified.explanation
        });
      }
      for (const subPurpose of subs) {
        const classified = routeFor(nodeType, purpose, subPurpose);
        entries.push({
          key: `${nodeType}/${purpose}/${subPurpose}`,
          nodeType,
          purpose,
          subPurpose,
          route: classified.route,
          explanation: classified.explanation
        });
      }
    }
  }
  return entries;
}
function refuse(explanation) {
  return { admit: false, code: NO_EXECUTABLE_ADAPTER, explanation };
}
function decideGenericJobAdmission(input) {
  const family = normalizeFamily(input.sourceFamily ?? input.nodeType);
  const purpose = input.purpose?.trim() || void 0;
  const subPurpose = input.subPurpose?.trim() || void 0;
  const modelId = input.modelId?.trim() || void 0;
  if (family && LOCAL_FAMILY.has(family)) return refuse(LOCAL_EXPLANATION);
  if (family === "sound" || family === "3d") return refuse(UNAVAILABLE_EXPLANATION[family]);
  if (family === "text-llm") {
    if (input.jobType === "TEXT_TO_IMAGE" || input.jobType === "IMAGE_TO_IMAGE" || input.jobType === "IMAGE_UPSCALE" || input.jobType === "VIDEO_GENERATION") {
      return refuse("Text streams through the AI API. It does not enter the generic image or video queue.");
    }
    return { admit: true, route: "direct" };
  }
  if (family === "image" || family === "video") {
    if (purpose && subPurpose) {
      const key = `${family}/${purpose}/${subPurpose}`;
      const excused = WEB_NODES_WITHOUT_OPERATION[key];
      if (excused && isStructuralNonOperation(excused)) return refuse(excused);
      if (excused && !modelId) return refuse(excused);
    }
    return { admit: true, route: "queued" };
  }
  if (!family) return { admit: true, route: "hosted" };
  return refuse(`No executable adapter is registered for ${family}.`);
}

// src/graph/nudge.ts
var NUDGE_IMAGE_WORKFLOWS = ["infl8", "action"];
var NUDGE_VIDEO_WORKFLOWS = ["time-slice", "time-wave", "time-slice-dual"];
var NUDGE_SUBJECT_MAX = 200;
var NUDGE_SUBJECT_MESSAGES = {
  infl8: "Use plain words for what to inflate",
  action: "Use plain words for what to turn into an action figure"
};
var REFUSED = /[\\{}]|\/\/|\/\*/;
function nudgeSubject(value) {
  if (typeof value !== "string") return null;
  const subject = value.trim();
  const length = [...subject].length;
  return length >= 1 && length <= NUDGE_SUBJECT_MAX && !REFUSED.test(subject) ? subject : null;
}
function nudgeSubjectIssue(workflow, value) {
  return nudgeSubject(value) === null ? NUDGE_SUBJECT_MESSAGES[workflow] : null;
}
function nudgePrompt(workflow, subject) {
  return workflow === "infl8" ? "inflate the <subject>".replace("<subject>", () => subject) : `action the ${subject}`;
}

// src/graph/tracy.ts
import { z as z4 } from "zod";
var TracyOptionsSchema = z4.object({
  detection: z4.enum(["luminance", "key-color"]).default("luminance"),
  keyColor: z4.string().regex(/^#[0-9a-f]{6}$/i).default("#ffffff"),
  threshold: z4.number().min(0).max(1).default(0.65),
  tolerance: z4.number().min(0).max(1).default(0.2),
  minArea: z4.number().min(1e-4).max(1).default(2e-3),
  maxRegions: z4.number().int().min(1).max(100).default(24),
  shape: z4.enum(["rectangle", "square", "ellipse", "circle"]).default("rectangle"),
  fill: z4.enum(["none", "hatch", "invert", "random-patch", "xray", "duotone", "glitch"]).default("none"),
  marker: z4.enum(["none", "dot", "plus", "cross", "polygon"]).default("plus"),
  sides: z4.number().int().min(3).max(12).default(4),
  markerFilled: z4.boolean().default(false),
  rotation: z4.number().min(-180).max(180).default(0),
  stretch: z4.boolean().default(false),
  grid: z4.enum(["none", "edge", "cartesian"]).default("edge"),
  scale: z4.enum(["pixels", "percent"]).default("pixels"),
  connection: z4.enum(["none", "spline", "pcb", "smooth", "step"]).default("pcb"),
  bend: z4.number().min(0).max(1).default(0.5),
  balance: z4.number().min(0).max(1).default(0.5),
  corner: z4.number().min(0).max(1).default(0.5),
  label: z4.enum(["none", "coordinates", "dimensions", "area", "id", "hex", "percent", "matrix"]).default("id"),
  font: z4.enum(["monospace", "sans-serif", "serif"]).default("monospace"),
  color: z4.string().regex(/^#[0-9a-f]{6}$/i).default("#a4ffda"),
  lineWidth: z4.number().min(0.5).max(8).default(1.5)
}).strict();
var TRACY_DEFAULTS = TracyOptionsSchema.parse({});
var TracyPresetSchema = z4.object({ schemaVersion: z4.literal(1), options: TracyOptionsSchema }).strict();
var TRACY_PRESETS = {
  "Product scan": TRACY_DEFAULTS,
  "Editorial grid": TracyOptionsSchema.parse({ shape: "circle", marker: "dot", grid: "cartesian", connection: "spline", label: "dimensions", color: "#ffffff" }),
  "Signal map": TracyOptionsSchema.parse({ fill: "hatch", marker: "polygon", sides: 6, connection: "step", label: "hex", color: "#ffbd69" }),
  "X-ray study": TracyOptionsSchema.parse({ fill: "xray", grid: "none", connection: "smooth", label: "matrix", color: "#8cddff" })
};
var TracyPreviewRequestSchema = z4.object({
  canvasId: z4.string().min(1).max(100),
  frameId: z4.string().min(1).max(100),
  options: TracyOptionsSchema
}).strict();

// src/graph/index.ts
var graphId = z5.string().min(1).max(160);
var coordinate = z5.number().finite();
var hexColor = z5.string().regex(/^#[0-9a-f]{6}$/i);
var StudioProviderConnectionSchema = z5.object({
  status: z5.enum(["missing", "ready", "invalid", "unavailable"]),
  message: z5.string()
});
var StudioParametersSchema = z5.record(z5.string(), z5.unknown()).superRefine((value, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  const visit = (item, depth) => {
    if (item === null || typeof item === "string" || typeof item === "boolean") return true;
    if (typeof item === "number") return Number.isFinite(item);
    if (typeof item !== "object" || depth > 64 || seen.has(item)) return false;
    if (!Array.isArray(item) && Object.getPrototypeOf(item) !== Object.prototype && Object.getPrototypeOf(item) !== null) return false;
    seen.add(item);
    const valid = Object.values(item).every((child) => visit(child, depth + 1));
    seen.delete(item);
    return valid;
  };
  if (!visit(value, 0)) ctx.addIssue({ code: "custom", message: "Graph settings must contain finite JSON values." });
});
var parameters = StudioParametersSchema;
var GraphRunOptionsSchema = z5.object({
  scope: z5.enum(["workflow", "node", "downstream", "selection"]).default("workflow"),
  nodeIds: z5.array(graphId).max(1e3).default([]),
  concurrency: z5.number().int().min(1).max(4).default(2),
  overrides: z5.record(graphId, StudioParametersSchema).default({}),
  wait: z5.boolean().default(false),
  runId: graphId.optional()
}).superRefine((value, ctx) => {
  if (value.scope !== "workflow" && !value.nodeIds.length) ctx.addIssue({ code: "custom", path: ["nodeIds"], message: "Choose nodes for this run." });
  if (value.scope === "node" && value.nodeIds.length !== 1) ctx.addIssue({ code: "custom", path: ["nodeIds"], message: "Run node takes exactly one node." });
});
var GraphPortSchema = z5.object({
  id: graphId,
  type: z5.enum(["image", "text", "any", "video", "audio", "mesh"])
}).loose();
var GraphExposedParameterSchema = z5.object({
  key: graphId,
  nodeId: graphId,
  field: graphId,
  type: z5.enum(["text", "number", "model"]),
  value: z5.unknown(),
  label: z5.string().max(500)
}).loose();
var GraphNodeDefSchema = z5.object({
  label: z5.string().min(1).max(500),
  category: z5.string().min(1).max(160),
  icon: z5.string().max(160),
  inputs: z5.array(GraphPortSchema).max(100),
  outputs: z5.array(GraphPortSchema).max(100),
  params: parameters,
  custom: z5.string().max(160).optional(),
  exposed: z5.array(GraphExposedParameterSchema).max(200).optional(),
  /** Kept so saved graphs still run, but no longer offered in the node palette. */
  legacy: z5.boolean().optional()
}).loose();
var GraphDocNodeSchema = z5.object({
  id: graphId,
  type: z5.string().min(1).max(200),
  x: coordinate,
  y: coordinate,
  params: parameters
}).loose();
var GraphDocEdgeSchema = z5.object({
  id: graphId,
  source: graphId,
  sourceHandle: z5.string().max(160).optional(),
  target: graphId,
  targetHandle: z5.string().max(160).optional()
}).loose();
var GraphDocSchema = z5.object({
  nodes: z5.array(GraphDocNodeSchema).max(1e4),
  edges: z5.array(GraphDocEdgeSchema).max(2e4)
}).loose().superRefine((doc, ctx) => {
  if (!parameters.safeParse(doc).success) ctx.addIssue({ code: "custom", message: "Graph documents must contain finite JSON values, including extension fields." });
  const nodeIds = /* @__PURE__ */ new Set();
  for (const [index, node] of doc.nodes.entries()) {
    if (nodeIds.has(node.id)) ctx.addIssue({ code: "custom", path: ["nodes", index, "id"], message: "Node IDs must be unique." });
    nodeIds.add(node.id);
  }
  const edgeIds = /* @__PURE__ */ new Set();
  for (const [index, edge] of doc.edges.entries()) {
    if (edgeIds.has(edge.id)) ctx.addIssue({ code: "custom", path: ["edges", index, "id"], message: "Edge IDs must be unique." });
    edgeIds.add(edge.id);
    for (const end of ["source", "target"]) {
      if (!nodeIds.has(edge[end])) ctx.addIssue({ code: "custom", path: ["edges", index, end], message: "An edge must refer to a node in this graph." });
    }
  }
});
function graphExecutionIssues(doc, definitions = GRAPH_NODE_DEFS) {
  const issues = [];
  const parsed = GraphDocSchema.safeParse(doc);
  if (!parsed.success) return parsed.error.issues.map((issue) => issue.message);
  const definition = (type) => Object.hasOwn(definitions, type) ? definitions[type] : void 0;
  const nodes = new Map(doc.nodes.map((node) => [node.id, node]));
  const indegree = new Map(doc.nodes.map((node) => [node.id, 0]));
  const outgoing = /* @__PURE__ */ new Map();
  for (const node of doc.nodes) {
    if (!definition(node.type)) issues.push(`Tool unavailable: ${node.type} (${node.id}).`);
  }
  for (const edge of doc.edges) {
    const source = nodes.get(edge.source);
    const target = nodes.get(edge.target);
    if (!source || !target) {
      issues.push(`Connection ${edge.id} refers to a missing node.`);
      continue;
    }
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    const targets = outgoing.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoing.set(edge.source, targets);
    const from = definition(source.type);
    const to = definition(target.type);
    if (!from || !to) continue;
    const output = from.outputs.find((port) => port.id === (edge.sourceHandle ?? "out"));
    const input = to.inputs.find((port) => port.id === (edge.targetHandle ?? "in"));
    if (!output || !input) issues.push(`Connection ${edge.id} uses an unavailable port.`);
    else if (output.type !== "any" && input.type !== "any" && output.type !== input.type) issues.push(`Connection ${edge.id} has incompatible port types.`);
  }
  const queue = doc.nodes.filter((node) => indegree.get(node.id) === 0).map((node) => node.id);
  for (let index = 0; index < queue.length; index++) {
    for (const target of outgoing.get(queue[index]) ?? []) {
      const remaining = (indegree.get(target) ?? 0) - 1;
      indegree.set(target, remaining);
      if (remaining === 0) queue.push(target);
    }
  }
  if (queue.length !== doc.nodes.length) issues.push("The graph contains a cycle.");
  return issues;
}
var StudioAssetSchema = z5.object({
  id: graphId,
  name: z5.string().min(1).max(500),
  mediaType: z5.enum(["image", "video", "audio", "mesh", "file"]),
  mimeType: z5.string().min(1).max(160),
  size: z5.number().int().nonnegative(),
  sha256: z5.string().regex(/^[a-f0-9]{64}$/)
}).loose();
var StudioProvenanceSchema = z5.object({
  graphId,
  nodeId: graphId,
  jobId: graphId.optional(),
  inputVersions: z5.array(z5.object({ frameId: graphId, versionId: graphId }).strict()).optional(),
  model: z5.string().max(500).optional(),
  settings: parameters.optional()
}).strict();
var StudioFrameVersionSchema = z5.object({
  id: graphId,
  label: z5.string().max(500),
  assetId: graphId.optional(),
  jobId: graphId.nullable().optional(),
  parentVersionId: graphId.nullable().optional(),
  params: parameters,
  provenance: StudioProvenanceSchema.optional()
}).loose();
var StudioFrameSchema = z5.object({
  id: graphId,
  title: z5.string().max(500),
  kind: z5.enum(["media", "board", "note", "section", "web", "design", "tool", "composition"]),
  x: coordinate,
  y: coordinate,
  w: z5.number().finite().positive(),
  h: z5.number().finite().positive(),
  activeVersion: z5.number().int().nonnegative(),
  params: parameters,
  versions: z5.array(StudioFrameVersionSchema).max(1e4)
}).loose().superRefine((frame, ctx) => {
  if (frame.activeVersion >= Math.max(frame.versions.length, 1)) {
    ctx.addIssue({ code: "custom", path: ["activeVersion"], message: "The active version must exist on this frame." });
  }
});
var StudioGraphSchema = z5.object({
  id: graphId,
  name: z5.string().min(1).max(500),
  doc: GraphDocSchema,
  source: z5.enum(["desktop", "web"]).optional()
}).loose();
var StudioProjectSchema = z5.object({
  schemaVersion: z5.literal(1),
  id: graphId,
  title: z5.string().min(1).max(500),
  artboards: z5.array(StudioFrameSchema).max(1e4),
  graphs: z5.array(StudioGraphSchema).max(1e3),
  assets: z5.array(StudioAssetSchema).max(2e4),
  primaryGraphId: graphId.optional()
}).loose().superRefine((project, ctx) => {
  if (!parameters.safeParse(project).success) ctx.addIssue({ code: "custom", message: "Projects must contain finite JSON values, including extension fields." });
  for (const key of ["artboards", "graphs", "assets"]) {
    const ids = /* @__PURE__ */ new Set();
    project[key].forEach((item, index) => {
      if (ids.has(item.id)) ctx.addIssue({ code: "custom", path: [key, index, "id"], message: `${key} IDs must be unique.` });
      ids.add(item.id);
    });
  }
  const graphs = new Map(project.graphs.map((graph) => [graph.id, graph]));
  const assets = new Set(project.assets.map((asset) => asset.id));
  const versions = /* @__PURE__ */ new Set();
  const frameVersions = new Map(project.artboards.map((frame) => [frame.id, new Set(frame.versions.map((version) => version.id))]));
  if (project.primaryGraphId && !graphs.has(project.primaryGraphId)) {
    ctx.addIssue({ code: "custom", path: ["primaryGraphId"], message: "The primary graph must belong to this project." });
  }
  project.artboards.forEach((frame, frameIndex) => frame.versions.forEach((version, versionIndex) => {
    const path = ["artboards", frameIndex, "versions", versionIndex];
    if (versions.has(version.id)) ctx.addIssue({ code: "custom", path: [...path, "id"], message: "Version IDs must be unique across the project." });
    versions.add(version.id);
    if (version.assetId && !assets.has(version.assetId)) ctx.addIssue({ code: "custom", path: [...path, "assetId"], message: "The version asset must be included in the project." });
    if (version.provenance) {
      const graph = graphs.get(version.provenance.graphId);
      if (!graph?.doc.nodes.some((node) => node.id === version.provenance?.nodeId)) ctx.addIssue({ code: "custom", path: [...path, "provenance"], message: "The producing node must belong to a project graph." });
    }
  }));
  const parents = new Map(project.artboards.flatMap((frame) => frame.versions.map((version) => [version.id, version.parentVersionId])));
  const checked = /* @__PURE__ */ new Set();
  for (const frame of project.artboards) {
    for (const version of frame.versions) {
      if (version.parentVersionId && !versions.has(version.parentVersionId)) {
        ctx.addIssue({ code: "custom", message: `Version ${version.id} refers to a missing parent version.` });
      }
      for (const input of version.provenance?.inputVersions ?? []) {
        if (!frameVersions.get(input.frameId)?.has(input.versionId)) {
          ctx.addIssue({ code: "custom", message: `Version ${version.id} refers to a missing input version.` });
        }
      }
      const chain = /* @__PURE__ */ new Set();
      let cursor = version.id;
      while (cursor && parents.has(cursor) && !checked.has(cursor)) {
        if (chain.has(cursor)) {
          ctx.addIssue({ code: "custom", message: "Frame version history contains a cycle." });
          break;
        }
        chain.add(cursor);
        cursor = parents.get(cursor);
      }
      for (const id of chain) checked.add(id);
    }
  }
});
var StudioWebNodeSchema = z5.object({
  id: graphId,
  type: z5.string().min(1).max(200),
  position: z5.object({ x: coordinate, y: coordinate }).loose(),
  data: parameters
}).loose();
var StudioWebEdgeSchema = z5.object({
  id: graphId,
  source: graphId,
  target: graphId,
  sourceHandle: z5.string().max(160).nullable().optional(),
  targetHandle: z5.string().max(160).nullable().optional()
}).loose();
var StudioWebPageSchema = z5.object({
  nodes: z5.array(StudioWebNodeSchema).max(1e4),
  edges: z5.array(StudioWebEdgeSchema).max(2e4)
}).loose();
var StudioDesktopVersionSchema = z5.object({
  id: graphId,
  label: z5.string().max(500),
  params: parameters,
  imagePath: z5.string().nullable().optional(),
  imageUrl: z5.string().nullable().optional(),
  jobId: graphId.nullable().optional(),
  parentVersionId: graphId.nullable().optional()
}).loose();
var StudioDesktopFrameSchema = z5.object({
  id: graphId,
  title: z5.string().max(500),
  kind: StudioFrameSchema.shape.kind,
  x: coordinate,
  y: coordinate,
  w: z5.number().finite().positive(),
  h: z5.number().finite().positive(),
  activeVersion: z5.number().int().nonnegative(),
  params: parameters,
  versions: z5.array(StudioDesktopVersionSchema)
}).loose();
var StudioDesktopSessionSchema = z5.object({
  id: graphId,
  title: z5.string().min(1).max(500),
  artboards: z5.array(StudioDesktopFrameSchema).max(1e4),
  graphs: z5.array(z5.object({ id: graphId, name: z5.string().min(1).max(500), doc: GraphDocSchema }).loose()).max(1e3)
}).loose();
var StudioFrameInputSchema = z5.object({ frameId: graphId, versionId: graphId }).strict();
var StudioFrameOutputSchema = z5.object({
  frameId: graphId,
  expectedVersionId: graphId.nullable(),
  version: StudioFrameVersionSchema
}).strict();
var StudioExecutionLocationSchema = z5.enum(["desktop", "cloud"]);
var StudioToolRequestSchema = z5.object({
  toolId: graphId,
  location: StudioExecutionLocationSchema,
  inputs: z5.array(StudioFrameInputSchema),
  params: parameters
}).strict();
var StudioToolResultSchema = z5.object({
  jobId: graphId,
  assets: z5.array(StudioAssetSchema)
}).strict();
var StudioSnapshotSchema = z5.object({
  revision: z5.number().int().nonnegative(),
  project: StudioProjectSchema
}).strict();
var StudioSaveSchema = z5.object({
  expectedRevision: z5.number().int().nonnegative(),
  project: StudioProjectSchema
}).strict();
var StudioAssetUploadSchema = z5.object({
  asset: StudioAssetSchema,
  base64: z5.string().max(32 * 1024 * 1024)
}).strict();
var frameSize = z5.number().finite().positive();
var FrameBoxSchema = z5.tuple([coordinate, coordinate, frameSize, frameSize]);
var FramePointSchema = z5.tuple([coordinate, coordinate]);
var FrameImageLevelSchema = z5.object({
  width: z5.number().int().positive(),
  image: z5.string().min(1).max(2e3)
}).strict();
var FieldClassRuleSchema = z5.object({
  /** The zone's colour over the picture, `#rrggbb`. */
  color: z5.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  /** Who may put a foot on it: everyone, no one, or only frames carrying `flag` (a boat on water). */
  feet: z5.enum(["all", "none", "flagged"]).optional(),
  flag: z5.string().regex(/^[\w-]{1,40}$/).optional(),
  /** Frames keep their paint off it (a tree, a building): a frame covering it breaks the rule. */
  keepClear: z5.boolean().optional(),
  note: z5.string().max(300).optional()
}).strict();
var FrameFieldSchema = z5.object({
  image: z5.string().min(1).max(2e3),
  width: z5.number().int().positive(),
  height: z5.number().int().positive(),
  /** Class maps: what each pixel value means. Code 0 is the base zone the eraser gives back to. */
  legend: z5.record(z5.string(), z5.string().max(200)).optional(),
  /** Class maps: each zone's rule, by code. */
  rules: z5.record(z5.string(), FieldClassRuleSchema).optional()
}).strict();
var FrameGroundSchema = z5.object({
  image: z5.string().min(1).max(2e3),
  small: z5.string().max(2e3).optional(),
  box: FrameBoxSchema,
  strength: z5.number().min(0).max(1).optional()
}).strict();
var FrameDeepZoomSchema = z5.object({
  id: z5.string().regex(/^[a-f0-9]{8}$/),
  /** The index page, an outputs path (`deepzoom/<id>/index.html`). */
  index: z5.string().max(200),
  at: z5.string().max(80),
  width: z5.number().int().positive(),
  height: z5.number().int().positive(),
  items: z5.number().int().min(0),
  stills: z5.boolean().optional()
}).strict();
var FrameLockSchema = z5.object({
  by: z5.enum(["hand", "import", "agent", "user"]).optional(),
  at: z5.string().max(80).optional(),
  note: z5.string().max(500).optional()
}).strict();
var FrameSidecarParamsSchema = z5.object({
  locked: z5.boolean().optional(),
  lock: FrameLockSchema.optional(),
  /** Drawn beneath every other frame, never selected or dragged (a plate, a backdrop). */
  background: z5.boolean().optional(),
  /** Draw order offset; the order is `layer + depth at the foot`. */
  layer: z5.number().finite().optional(),
  /** Where the frame stands, relative to its box. */
  foot: FramePointSchema.optional(),
  /** 0 far to 1 near, the last sampled depth at the foot (the canvas re-samples when it has the field). */
  depth: z5.number().min(0).max(1).optional(),
  /** A small copy (about 256 px) drawn while the frame is small on screen; also its hit map. */
  small: z5.string().max(2e3).optional(),
  /** The last mask painted on the frame (white on black, an outputs file), kept for the next inpaint. */
  mask: z5.string().max(2e3).optional(),
  pyramid: z5.array(FrameImageLevelSchema).max(16).optional(),
  ground: FrameGroundSchema.optional(),
  fields: z5.object({ depth: FrameFieldSchema.optional(), classes: FrameFieldSchema.optional() }).strict().optional(),
  /** Audit flags, free strings (`bg`, `cut`, `tone`, …). */
  flags: z5.array(z5.string().max(80)).max(64).optional(),
  deepZoom: FrameDeepZoomSchema.optional(),
  /** Project data that is not Genie's business (a number, a topic, a panel name). */
  meta: z5.record(z5.string(), z5.union([z5.string().max(4e3), z5.number(), z5.boolean(), z5.null()])).optional(),
  /** Where an imported frame came from. */
  origin: z5.object({ source: z5.string().max(2e3), key: z5.string().max(160).optional(), importedAt: z5.string().max(80).optional() }).strict().optional()
});
var ZONE_STANDABLE = /ground|water|floor|road|sand|grass|path|earth|field|snow|street|beach|soil|dirt|lawn|meadow|pond/i;
var ZONE_SKY = /\bsky\b|cloud|heaven/i;
var ZONE_TREES = /tree|bush|hedge|shrub|plant|foliage|wood|forest/i;
var ZONE_COLORS = [
  [ZONE_SKY, "#966eff"],
  [/water|lake|river|sea/i, "#288cff"],
  [ZONE_TREES, "#ff3c3c"],
  [/building|tower|wall|house|rock|solid|stone/i, "#ff961e"],
  [/pond/i, "#00e6d2"],
  [ZONE_STANDABLE, "#ebc85a"]
];
var ZONE_SPARE = ["#e93d82", "#8e4ec6", "#12a594", "#f7d544", "#46a758", "#4c9ffb"];
function zoneRule(field, code) {
  const name = field?.legend?.[String(code)] ?? (code === 0 ? "ground" : `zone ${code}`);
  const set = field?.rules?.[String(code)] ?? {};
  const sky = ZONE_SKY.test(name), ground = !sky && ZONE_STANDABLE.test(name);
  const color = set.color ?? ZONE_COLORS.find(([pattern]) => pattern.test(name))?.[1] ?? ZONE_SPARE[code % ZONE_SPARE.length];
  const feet = set.feet ?? (ground ? "all" : "none");
  return {
    code,
    name,
    color,
    feet,
    ...feet === "flagged" && set.flag ? { flag: set.flag } : {},
    keepClear: set.keepClear ?? (!sky && !ground),
    ...set.note ? { note: set.note } : {}
  };
}
function zoneRules(field) {
  const codes = /* @__PURE__ */ new Set([0]);
  for (const key of [...Object.keys(field?.legend ?? {}), ...Object.keys(field?.rules ?? {})]) {
    const code = Number(key);
    if (Number.isInteger(code) && code >= 0 && code < 256) codes.add(code);
  }
  return [...codes].sort((a, b) => a - b).map((code) => zoneRule(field, code));
}
function zoneTakesFeet(rule, flags = []) {
  return rule.feet === "all" || rule.feet === "flagged" && Boolean(rule.flag) && flags.includes(rule.flag);
}
function frameOrder(params, depthAtFoot) {
  const depth = typeof depthAtFoot === "number" && Number.isFinite(depthAtFoot) ? depthAtFoot : params.depth ?? 0;
  return (params.layer ?? 0) + depth;
}
var CanvasImportVersionSchema = z5.object({
  label: z5.string().max(200),
  image: z5.string().min(1).max(2e3),
  at: z5.string().max(80).optional(),
  /** This version's own sidecars (its ground, its box) and any project data. */
  params: parameters.optional()
}).strict();
var CanvasImportFrameSchema = z5.object({
  /** Stable within the folder; becomes the frame's file folder in the outputs. */
  key: z5.string().regex(/^[\w-]{1,120}$/),
  title: z5.string().max(500),
  kind: z5.enum(["media", "note", "section"]).optional(),
  x: coordinate,
  y: coordinate,
  w: frameSize,
  h: frameSize,
  /** Pixel size of the picture when it differs from the box (a plate that exists only as tiles). */
  width: z5.number().int().positive().optional(),
  height: z5.number().int().positive().optional(),
  params: FrameSidecarParamsSchema.optional(),
  versions: z5.array(CanvasImportVersionSchema).max(1e4),
  /** Index into `versions`; the last one when absent. */
  activeVersion: z5.number().int().nonnegative().optional()
}).strict().superRefine((frame, ctx) => {
  if (frame.activeVersion !== void 0 && frame.activeVersion >= Math.max(frame.versions.length, 1)) {
    ctx.addIssue({ code: "custom", path: ["activeVersion"], message: "The active version must exist on this frame." });
  }
});
var CanvasImportSchema = z5.object({
  version: z5.literal(1),
  title: z5.string().min(1).max(500),
  note: z5.string().max(4e3).optional(),
  /** The document's extent: the Board's first fit, or the scene size when the frames go inside a Compositor. */
  size: z5.object({ width: frameSize, height: frameSize }).strict().optional(),
  /**
   * `board` (default): every frame lands on the Board. `composition`: the Board gets one Compositor
   * frame named after the document and every frame goes inside it as a layer, stacked back to front
   * by its draw order (background first, then layer + depth), so the wall is one thing on the Board.
   */
  container: z5.enum(["board", "composition"]).optional(),
  /** The scene's colour behind everything (composition container only). */
  background: hexColor.optional(),
  frames: z5.array(CanvasImportFrameSchema).max(1e4)
}).strict().superRefine((doc, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  doc.frames.forEach((frame, index) => {
    if (seen.has(frame.key)) ctx.addIssue({ code: "custom", path: ["frames", index, "key"], message: `Frame key "${frame.key}" is used twice.` });
    seen.add(frame.key);
  });
  if (doc.container === "composition") {
    if (doc.frames.length > COMPOSITION_MAX_LAYERS) ctx.addIssue({ code: "custom", path: ["frames"], message: `A Compositor holds at most ${COMPOSITION_MAX_LAYERS} layers.` });
    doc.frames.forEach((frame, index) => {
      if (frame.kind && frame.kind !== "media") ctx.addIssue({ code: "custom", path: ["frames", index, "kind"], message: "Only media frames can go inside a Compositor." });
    });
  }
});
var ArtCanvasFrameSchema = z5.object({
  id: graphId,
  title: z5.string(),
  kind: StudioFrameSchema.shape.kind,
  /** The Compositor frame this one lives in. A frame inside a Compositor is one of its layers, not a Board frame; its x, y, w, h are scene pixels. */
  parentId: graphId.optional(),
  x: coordinate,
  y: coordinate,
  w: z5.number().positive(),
  h: z5.number().positive(),
  activeVersion: z5.number().int().nonnegative(),
  versions: z5.array(z5.object({
    id: graphId,
    label: z5.string(),
    gradient: z5.string(),
    image: z5.string().optional(),
    mediaType: StudioAssetSchema.shape.mediaType.optional(),
    error: z5.string().optional()
  })),
  params: z5.object({
    width: z5.number().optional(),
    height: z5.number().optional(),
    text: z5.string().optional(),
    url: z5.string().optional(),
    favorite: z5.boolean().optional(),
    values: z5.record(z5.string(), z5.union([z5.string(), z5.number(), z5.boolean()])).optional(),
    /** A web frame whose page takes value changes by message (`genie:values`) instead of reloading. */
    liveValues: z5.boolean().optional()
  }).extend(FrameSidecarParamsSchema.shape),
  job: z5.object({
    id: graphId,
    status: z5.string(),
    progress: z5.number(),
    error: z5.string().optional(),
    preview: z5.string().optional()
  }).optional()
});
var ArtCanvasLineageSchema = z5.object({
  canvasId: graphId,
  nodes: z5.array(z5.object({
    id: graphId,
    kind: z5.enum(["op", "output"]),
    label: z5.string(),
    detail: z5.string().optional(),
    artboardId: graphId.optional(),
    idx: z5.number().optional(),
    imageUrl: z5.string().nullable().optional()
  })),
  edges: z5.array(z5.tuple([graphId, graphId]))
});
var StudioWorkflowImageSchema = z5.object({
  id: graphId,
  url: z5.string(),
  label: z5.string(),
  jobId: graphId.optional(),
  inputVersions: z5.array(StudioFrameInputSchema).optional(),
  settings: StudioParametersSchema.optional(),
  model: z5.string().optional()
});
var StudioMediaFileSchema = z5.object({
  uuid: z5.string(),
  filename: z5.string(),
  contentType: z5.string(),
  size: z5.number(),
  publicUrl: z5.string().nullable(),
  nodeId: z5.string().nullable(),
  folderId: z5.string().nullable(),
  status: z5.enum(["ACTIVE", "PROCESSING", "FAILED", "DELETED"]),
  metadata: z5.record(z5.string(), z5.unknown()).optional(),
  createdAt: z5.string(),
  updatedAt: z5.string().optional()
});
var StudioMediaFolderSchema = z5.object({
  id: z5.string(),
  uuid: z5.string(),
  name: z5.string(),
  fileCount: z5.number(),
  createdAt: z5.string(),
  updatedAt: z5.string()
});
var StudioWalletTransactionSchema = z5.object({
  id: z5.string(),
  // Mirrors WalletTransactionType in packages/db/prisma/schema.prisma.
  type: z5.enum(["JOB_DEDUCTION", "TOP_UP", "REFUND", "SUBSCRIPTION_CREDIT", "TRAINING_HOLD", "TRAINING_CONFIRMED", "TRAINING_REFUND", "ADMIN_DEBIT", "REFERRAL_CREDIT"]),
  amount: z5.string(),
  balanceAfter: z5.string(),
  jobId: z5.string().nullable(),
  description: z5.string().nullable(),
  createdAt: z5.string()
});
var StudioNodePageSnapshotSchema = z5.object({
  revision: z5.number().int().nonnegative(),
  page: StudioWebPageSchema
}).strict();
var StudioNodePageSaveSchema = z5.object({
  expectedRevision: z5.number().int().nonnegative(),
  page: StudioWebPageSchema
}).strict();
var StudioNodeOutputWriteSchema = z5.object({
  output: StudioFrameOutputSchema,
  page: StudioWebPageSchema,
  graphId,
  dataUrl: z5.string().max(34 * 1024 * 1024).regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/)
}).strict();
var StudioQueueStateSchema = z5.object({
  paused: z5.boolean(),
  activeId: z5.string().nullable(),
  pendingIds: z5.array(z5.string()),
  heldIds: z5.array(z5.string()),
  revision: z5.number().int().nonnegative()
});
var StudioQueuePauseSchema = z5.object({ paused: z5.boolean() }).strict();
var StudioQueueHoldSchema = z5.object({ held: z5.boolean() }).strict();
var StudioQueueMoveSchema = z5.object({
  id: z5.string().min(1),
  beforeId: z5.string().min(1).nullable(),
  expectedRevision: z5.number().int().nonnegative()
}).strict();
var StudioActivityStatusSchema = z5.enum(["idle", "queued", "paused", "processing", "success", "error", "cancelled"]);
var StudioActivityActionSchema = z5.enum(["cancel", "retry", "pause", "resume", "move-up", "move-down"]);
var StudioActivityItemSchema = z5.object({
  id: z5.string(),
  title: z5.string(),
  description: z5.string().optional(),
  timestamp: z5.date().optional(),
  status: StudioActivityStatusSchema,
  nodeType: z5.string().optional(),
  errorMessage: z5.string().optional(),
  progress: z5.number().min(0).max(100).optional(),
  actions: z5.array(StudioActivityActionSchema).optional(),
  details: z5.array(z5.object({ label: z5.string(), value: z5.string() })).optional()
});
var LIBRARY_MAX_FILE_SIZE = 250 * 1024 * 1024;
var libraryFilename = z5.string().trim().min(1).max(255).refine(
  (value) => Array.from(value).every((character) => character !== "/" && character !== "\\" && character.charCodeAt(0) >= 32),
  "Use a filename without slashes or control characters."
);
var LibraryAssetSchema = StudioAssetSchema.pick({ id: true, name: true, mediaType: true, mimeType: true, size: true, sha256: true }).extend({
  id: z5.string().regex(/^[a-f0-9]{64}$/),
  name: libraryFilename,
  size: z5.number().int().nonnegative().max(LIBRARY_MAX_FILE_SIZE),
  mimeType: z5.string().regex(/^[a-z]+\/[a-z0-9.+-]+$/i).max(160)
}).strict().refine((asset) => asset.id === asset.sha256, "File identity must match its checksum.");
var LibraryRecordSchema = z5.object({
  asset: LibraryAssetSchema,
  revision: z5.number().int().positive(),
  createdAt: z5.string().datetime(),
  updatedAt: z5.string().datetime(),
  deletedAt: z5.string().datetime().nullable()
}).strict();
var LibraryPageSchema = z5.object({ files: z5.array(LibraryRecordSchema).max(100), nextCursor: z5.string().nullable() }).strict();
var LibraryChangeSchema = z5.object({
  expectedRevision: z5.number().int().positive(),
  name: libraryFilename.optional(),
  deleted: z5.boolean().optional()
}).strict();
var LibrarySyncStatusSchema = z5.object({
  state: z5.enum(["signed-out", "syncing", "synced", "offline", "error"]),
  pending: z5.number().int().nonnegative(),
  lastSyncedAt: z5.string().nullable(),
  error: z5.string().nullable()
}).strict();
var LibraryIndexStatusSchema = z5.object({ state: z5.enum(["pending", "ready", "error"]), error: z5.string().nullable() }).strict();
var LibraryDeviceFileSchema = z5.object({
  asset: LibraryAssetSchema,
  revision: z5.number().int().nonnegative(),
  createdAt: z5.string(),
  localUrl: z5.string().nullable(),
  state: z5.enum(["pending", "synced"])
}).strict();
var LibraryDeviceListingSchema = z5.object({ files: z5.array(LibraryDeviceFileSchema), status: LibrarySyncStatusSchema }).strict();
var COMPOSITION_BLENDS = ["normal", "multiply", "screen", "overlay", "darken", "lighten"];
var seconds = z5.number().finite().min(0).max(3600);
var COMPOSITION_MODEL_MODES = ["original", "pointCloud", "normal", "wireframe", "depth", "clay"];
var COMPOSITION_UP_DIRECTIONS = ["original", "-x", "+x", "-y", "+y", "-z", "+z"];
var vec3 = z5.tuple([coordinate, coordinate, coordinate]);
var CompositionCameraSchema = z5.object({
  position: vec3,
  target: vec3,
  zoom: z5.number().finite().positive().max(1e6),
  cameraType: z5.enum(["perspective", "orthographic"]),
  fov: z5.number().finite().min(1).max(179)
}).strict();
var CompositionLayer3DSchema = z5.object({
  camera: CompositionCameraSchema.optional(),
  /** Turns around the up axis across the clip; 0 holds the start camera. */
  spin: z5.number().finite().min(-20).max(20).optional(),
  lightIntensity: z5.number().finite().min(0).max(5).optional(),
  /** 'transparent' lets the layers below show through. */
  background: z5.union([z5.literal("transparent"), hexColor]).optional(),
  grid: z5.boolean().optional(),
  mode: z5.enum(COMPOSITION_MODEL_MODES).optional(),
  upDirection: z5.enum(COMPOSITION_UP_DIRECTIONS).optional(),
  /** Reserved for animation clips; no control writes these yet. */
  clip: z5.string().max(200).nullable().optional(),
  clipRate: z5.number().finite().min(0).max(4).optional()
}).strict();
var CompositionLayerSchema = z5.object({
  id: graphId,
  name: z5.string().min(1).max(200),
  type: z5.enum(["image", "video", "audio", "model", "text"]),
  /** The canvas frame the media came from, if any. */
  frameId: graphId.optional(),
  /** Media URL; a 3D layer points at the model file itself. */
  src: z5.string().min(1).max(4096).optional(),
  /** 3D layers: how the model is framed, lit and turned. Absent means the viewer's defaults. */
  three: CompositionLayer3DSchema.optional(),
  text: z5.string().max(2e3).optional(),
  fontFamily: z5.string().max(120).optional(),
  fontSize: z5.number().finite().min(4).max(2e3).optional(),
  fontWeight: z5.enum(["normal", "bold"]).optional(),
  color: hexColor.optional(),
  align: z5.enum(["left", "center", "right"]).optional(),
  x: coordinate,
  y: coordinate,
  width: z5.number().finite().positive().max(1e5),
  height: z5.number().finite().positive().max(1e5),
  rotation: z5.number().finite().min(-360).max(360),
  opacity: z5.number().finite().min(0).max(1),
  blend: z5.enum(COMPOSITION_BLENDS),
  /** When the layer appears on the timeline. */
  start: seconds,
  duration: z5.number().finite().positive().max(3600),
  /** How far into its source a video or audio layer begins playing. */
  offset: seconds,
  volume: z5.number().finite().min(0).max(1),
  muted: z5.boolean(),
  hidden: z5.boolean(),
  locked: z5.boolean(),
  /** Layers that share a group id are picked, moved and stacked as one; they sit together in the stack. */
  group: z5.string().min(1).max(64).optional()
}).strict().superRefine((layer, ctx) => {
  if (layer.type === "text" && layer.text === void 0) ctx.addIssue({ code: "custom", path: ["text"], message: "A text layer needs text." });
  if (layer.type !== "text" && !layer.src) ctx.addIssue({ code: "custom", path: ["src"], message: "A media layer needs a source." });
  if (layer.three && layer.type !== "model") ctx.addIssue({ code: "custom", path: ["three"], message: "Only a 3D layer has 3D settings." });
});
var COMPOSITION_MAX_SIZE = 65536;
var COMPOSITION_MAX_LAYERS = 4096;
var CompositionSchema = z5.object({
  width: z5.number().int().min(16).max(COMPOSITION_MAX_SIZE),
  height: z5.number().int().min(16).max(COMPOSITION_MAX_SIZE),
  fps: z5.number().int().min(1).max(120),
  duration: z5.number().finite().min(0.1).max(3600),
  background: hexColor,
  layers: z5.array(CompositionLayerSchema).max(COMPOSITION_MAX_LAYERS)
}).strict().superRefine((composition, ctx) => {
  const ids = /* @__PURE__ */ new Set();
  composition.layers.forEach((layer, index) => {
    if (ids.has(layer.id)) ctx.addIssue({ code: "custom", path: ["layers", index, "id"], message: "Layer IDs must be unique." });
    ids.add(layer.id);
  });
});

export {
  TIMESLICE_CLASS,
  TIMESLICE_NODE_BY_WORKFLOW,
  TIMESLICE_OPTION_LABELS,
  TIMESLICE_PARAMS,
  TIMESLICE_DEFAULTS,
  TimeSliceEffectParamsSchema,
  TimeSliceWaveParamsSchema,
  TimeSliceDualParamsSchema,
  roundHalfEven,
  timesliceEase,
  NumpyRandomState,
  sliceOffsets,
  resolveFrame,
  sourcePattern,
  biasedSourceMap,
  planEffect,
  planWave,
  planDual,
  timesliceMemoryBytes,
  GENERATION_OPERATIONS,
  GRAPH_NODE_DEFS,
  APP_OPERATIONS,
  graphNodeDef,
  customWorkflowDef,
  CUSTOM_NODE_PREFIX,
  resolveNodeDef,
  graphNodeDefs,
  DesktopUpdateStateSchema,
  SceneAssetSchema,
  SceneEnvironmentSchema,
  SceneMaterialSchema,
  SceneLibrarySettingsSchema,
  ApplyLibraryAssetSchema,
  ShapeOptionsSchema,
  WEB_NODE_TYPES,
  WEB_CANVAS_NODES,
  WEB_NODES_WITHOUT_OPERATION,
  webNodeOperation,
  WEB_NODE_OPERATIONS,
  webOperationIssues,
  NO_EXECUTABLE_ADAPTER,
  LOCAL_CANVAS_FAMILIES,
  isStructuralNonOperation,
  executionInventory,
  decideGenericJobAdmission,
  NUDGE_IMAGE_WORKFLOWS,
  NUDGE_VIDEO_WORKFLOWS,
  NUDGE_SUBJECT_MAX,
  NUDGE_SUBJECT_MESSAGES,
  nudgeSubject,
  nudgeSubjectIssue,
  nudgePrompt,
  TracyOptionsSchema,
  TRACY_DEFAULTS,
  TracyPresetSchema,
  TRACY_PRESETS,
  TracyPreviewRequestSchema,
  StudioProviderConnectionSchema,
  StudioParametersSchema,
  GraphRunOptionsSchema,
  GraphPortSchema,
  GraphExposedParameterSchema,
  GraphNodeDefSchema,
  GraphDocNodeSchema,
  GraphDocEdgeSchema,
  GraphDocSchema,
  graphExecutionIssues,
  StudioAssetSchema,
  StudioProvenanceSchema,
  StudioFrameVersionSchema,
  StudioFrameSchema,
  StudioGraphSchema,
  StudioProjectSchema,
  StudioWebNodeSchema,
  StudioWebEdgeSchema,
  StudioWebPageSchema,
  StudioDesktopVersionSchema,
  StudioDesktopFrameSchema,
  StudioDesktopSessionSchema,
  StudioFrameInputSchema,
  StudioFrameOutputSchema,
  StudioExecutionLocationSchema,
  StudioToolRequestSchema,
  StudioToolResultSchema,
  StudioSnapshotSchema,
  StudioSaveSchema,
  StudioAssetUploadSchema,
  FrameBoxSchema,
  FramePointSchema,
  FrameImageLevelSchema,
  FieldClassRuleSchema,
  FrameFieldSchema,
  FrameGroundSchema,
  FrameDeepZoomSchema,
  FrameLockSchema,
  FrameSidecarParamsSchema,
  ZONE_STANDABLE,
  ZONE_SKY,
  ZONE_TREES,
  zoneRule,
  zoneRules,
  zoneTakesFeet,
  frameOrder,
  CanvasImportVersionSchema,
  CanvasImportFrameSchema,
  CanvasImportSchema,
  ArtCanvasFrameSchema,
  ArtCanvasLineageSchema,
  StudioWorkflowImageSchema,
  StudioMediaFileSchema,
  StudioMediaFolderSchema,
  StudioWalletTransactionSchema,
  StudioNodePageSnapshotSchema,
  StudioNodePageSaveSchema,
  StudioNodeOutputWriteSchema,
  StudioQueueStateSchema,
  StudioQueuePauseSchema,
  StudioQueueHoldSchema,
  StudioQueueMoveSchema,
  StudioActivityStatusSchema,
  StudioActivityActionSchema,
  StudioActivityItemSchema,
  LIBRARY_MAX_FILE_SIZE,
  LibraryAssetSchema,
  LibraryRecordSchema,
  LibraryPageSchema,
  LibraryChangeSchema,
  LibrarySyncStatusSchema,
  LibraryIndexStatusSchema,
  LibraryDeviceFileSchema,
  LibraryDeviceListingSchema,
  COMPOSITION_BLENDS,
  COMPOSITION_MODEL_MODES,
  COMPOSITION_UP_DIRECTIONS,
  CompositionCameraSchema,
  CompositionLayer3DSchema,
  CompositionLayerSchema,
  COMPOSITION_MAX_SIZE,
  COMPOSITION_MAX_LAYERS,
  CompositionSchema
};
//# sourceMappingURL=chunk-NAXZHGEO.js.map